import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useId,
  useLayoutEffect,
} from 'react';
import { createPortal } from 'react-dom';
import { useEscapeToClose } from '../../../hooks/useEscapeToClose';
import styles from './SearchableSelect.module.css';

export interface SelectOption {
  value: string;
  label: string;
  group?: string;
  disabled?: boolean;
}

interface SearchableSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  loading?: boolean;
  hasError?: boolean;
  readOnly?: boolean;
  /** Custom renderer for the trigger label when an option is selected */
  triggerFormat?: (option: SelectOption) => React.ReactNode;
  /** Custom renderer for each option inside the dropdown list */
  optionFormat?: (option: SelectOption) => React.ReactNode;
  className?: string;
  noResultsText?: string;
  /** When false, hides the empty "Nenhuma opção" choice. Defaults to true. */
  allowEmpty?: boolean;
  /**
   * When set, search is delegated to the parent (server-side).
   * Local filtering is skipped and the parent should update `options`.
   */
  onSearchChange?: (query: string) => void;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Selecione...',
  searchPlaceholder = 'Buscar...',
  disabled = false,
  loading = false,
  hasError = false,
  readOnly = false,
  triggerFormat,
  optionFormat,
  className,
  noResultsText = 'Nenhum resultado encontrado',
  allowEmpty = true,
  onSearchChange,
}) => {
  const BLANK_OPTION: SelectOption = useMemo(() => ({ value: '', label: 'Nenhuma opção' }), []);
  const id = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties | null>(null);

  const normalizedOptions = useMemo(() => {
    const optionsWithoutBlank = options.filter((option) => option.value !== '');

    if (!allowEmpty) {
      return optionsWithoutBlank;
    }

    return [BLANK_OPTION, ...optionsWithoutBlank];
  }, [BLANK_OPTION, allowEmpty, options]);

  const selected = useMemo(
    () => normalizedOptions.find((o) => o.value === value && o.value !== '') ?? null,
    [normalizedOptions, value]
  );

  const filtered = useMemo(() => {
    if (onSearchChange) {
      return normalizedOptions;
    }

    if (!query.trim()) {
      return normalizedOptions;
    }

    const q = query.toLowerCase();
    const searchableOptions = allowEmpty ? normalizedOptions.slice(1) : normalizedOptions;

    return searchableOptions.filter(
      (option) =>
        option.label.toLowerCase().includes(q) || option.value.toLowerCase().includes(q),
    );
  }, [allowEmpty, normalizedOptions, onSearchChange, query]);

  useEffect(() => {
    if (!onSearchChange || !open) {
      return;
    }

    onSearchChange(query);
  }, [onSearchChange, open, query]);

  const closeDropdown = useCallback(() => {
    setOpen(false);
    setQuery('');
  }, []);
  useEscapeToClose(closeDropdown, open);

  const updateDropdownPosition = useCallback(() => {
    const triggerEl = triggerRef.current;

    if (!triggerEl) {
      return;
    }

    const viewportPadding = 8;
    const dropdownOffset = 4;
    const searchBarHeight = 41;
    const optionHeight = 36;
    const listPadding = 8;
    const rect = triggerEl.getBoundingClientRect();
    const estimatedHeight = searchBarHeight + listPadding + Math.max(filtered.length, 1) * optionHeight;
    const dropdownHeight = dropdownRef.current?.offsetHeight || estimatedHeight;
    const dropdownWidth = Math.max(rect.width, 240);
    const viewportHeight = window.innerHeight;
    const unclampedLeft = rect.left;
    const maxLeft = window.innerWidth - dropdownWidth - viewportPadding;
    const left = Math.min(Math.max(unclampedLeft, viewportPadding), Math.max(viewportPadding, maxLeft));

    const spaceBelow = viewportHeight - rect.bottom - viewportPadding;
    const spaceAbove = rect.top - viewportPadding;
    const shouldOpenUp = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

    const preferredTop = shouldOpenUp
      ? rect.top - dropdownHeight - dropdownOffset
      : rect.bottom + dropdownOffset;
    const maxTop = viewportHeight - dropdownHeight - viewportPadding;
    const top = Math.max(viewportPadding, Math.min(preferredTop, maxTop));

    setDropdownStyle({
      top,
      left,
      width: dropdownWidth,
      visibility: 'visible',
    });
  }, [filtered.length]);

  /* Focus search input when opening — only on desktop.
   * On mobile/touch, autofocus would open the keyboard immediately. */
  useEffect(() => {
    if (!open) return undefined;

    const canHover = typeof window.matchMedia === 'function'
      && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    let timeoutId: number | undefined;
    if (canHover) {
      timeoutId = window.setTimeout(() => inputRef.current?.focus(), 30);
    }

    /* Scroll selected item into view */
    const listEl = listRef.current;
    if (listEl) {
      const activeEl = listEl.querySelector('[data-active="true"]') as HTMLElement | null;
      if (activeEl) activeEl.scrollIntoView({ block: 'nearest' });
    }

    return () => {
      if (timeoutId != null) window.clearTimeout(timeoutId);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      setDropdownStyle(null);
      return undefined;
    }

    updateDropdownPosition();
    const frame = window.requestAnimationFrame(updateDropdownPosition);

    return () => window.cancelAnimationFrame(frame);
  }, [open, filtered.length, updateDropdownPosition]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;

      if (containerRef.current?.contains(target) || dropdownRef.current?.contains(target)) {
        return;
      }

      closeDropdown();
    };

    const handleWindowChange = () => {
      updateDropdownPosition();
    };

    document.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('resize', handleWindowChange);
    window.addEventListener('scroll', handleWindowChange, true);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('resize', handleWindowChange);
      window.removeEventListener('scroll', handleWindowChange, true);
    };
  }, [closeDropdown, open, updateDropdownPosition]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeDropdown();
      }
    },
    [closeDropdown]
  );

  const handleSelect = useCallback(
    (opt: SelectOption) => {
      onChange(opt.value);
      closeDropdown();
    },
    [closeDropdown, onChange]
  );

  const triggerLabel = selected
    ? (triggerFormat ? triggerFormat(selected) : selected.label)
    : null;
  const resolvedPlaceholder = value === '' && allowEmpty ? 'Selecionar' : placeholder;

  const triggerCls = [
    styles.trigger,
    open ? styles.triggerOpen : '',
    hasError ? styles.triggerError : '',
    disabled ? styles.triggerDisabled : '',
    readOnly ? styles.triggerReadOnly : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  const dropdownContent = open ? (
    <div
      ref={dropdownRef}
      className={styles.dropdown}
      style={dropdownStyle ?? { visibility: 'hidden', top: 0, left: 0, pointerEvents: 'none' }}
      role="dialog"
      aria-labelledby={id}
      onKeyDown={handleKeyDown}
    >
      <div className={styles.searchWrapper}>
        <span className={`${styles.searchIcon} material-symbols-outlined`} aria-hidden="true">
          search
        </span>
        <input
          ref={inputRef}
          className={styles.searchInput}
          type="text"
          placeholder={searchPlaceholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {query && (
          <button
            type="button"
            className={styles.clearBtn}
            onClick={() => setQuery('')}
            tabIndex={-1}
            aria-label="Limpar busca"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        )}
      </div>

      <ul ref={listRef} className={styles.list} role="listbox">
        {filtered.length === 0 ? (
          <li className={styles.noResults}>
            {loading ? 'Carregando...' : noResultsText}
          </li>
        ) : (
          filtered.map((opt, index) => {
            const isSelected = opt.value === value;
            const isDisabled = Boolean(opt.disabled);
            const previousOption = index > 0 ? filtered[index - 1] : null;
            const showGroupHeader = Boolean(opt.group) && opt.group !== previousOption?.group;
            return (
              <React.Fragment key={opt.value}>
                {showGroupHeader && (
                  <li className={styles.groupHeader} role="presentation">{opt.group}</li>
                )}
                <li
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={isDisabled}
                  data-active={isSelected ? 'true' : undefined}
                  className={`${styles.option} ${isSelected ? styles.optionSelected : ''} ${isDisabled ? styles.optionDisabled : ''}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (!isDisabled) {
                      handleSelect(opt);
                    }
                  }}
                >
                  {opt.value === '' ? 'Nenhuma opção' : (optionFormat ? optionFormat(opt) : opt.label)}
                  {isSelected && (
                    <span className={`${styles.checkIcon} material-symbols-outlined`} aria-hidden="true">
                      check
                    </span>
                  )}
                </li>
              </React.Fragment>
            );
          })
        )}
      </ul>
    </div>
  ) : null;

  return (
    <div className={styles.wrapper} ref={containerRef} onKeyDown={handleKeyDown}>
      <button
        id={id}
        ref={triggerRef}
        type="button"
        className={triggerCls}
        onClick={() => !disabled && !loading && !readOnly && setOpen((o) => !o)}
        disabled={disabled || loading}
        aria-disabled={disabled || loading || readOnly}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={triggerLabel ? styles.triggerValue : styles.triggerPlaceholder}>
          {triggerLabel ?? (loading ? 'Carregando...' : resolvedPlaceholder)}
        </span>
        <span
          className={`${styles.chevron} ${open ? styles.chevronUp : ''} material-symbols-outlined`}
          aria-hidden="true"
        >
          expand_more
        </span>
      </button>
      {dropdownContent && createPortal(dropdownContent, document.body)}
    </div>
  );
};

export default SearchableSelect;
